import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Camera as CameraIcon, Upload, Video, X } from 'lucide-react'
import { ensureCameraPermission } from '../../utils/devicePermissions.js'
import { isNativeApp } from '../../utils/nativeApp.js'

const PHOTO_ACCEPT = 'image/*,image/jpeg,image/jpg,image/png,image/gif,image/webp,image/heic,image/heif'
const VIDEO_ACCEPT = 'video/*,video/mp4,video/webm,video/quicktime,video/x-m4v,video/3gpp'
const ACCEPT = `${PHOTO_ACCEPT},${VIDEO_ACCEPT}`
const MAX_PHOTOS = 10
const MAX_VIDEOS = 5
const MAX_PHOTO_BYTES = 5 * 1024 * 1024
const MAX_VIDEO_BYTES = 50 * 1024 * 1024
const CAPTURE_MQ = '(max-width: 1366px), (pointer: coarse)'

const isVideoFile = (file) =>
  file?.type?.startsWith('video/') || /\.(mp4|webm|mov|m4v|3gp)$/i.test(file?.name || '')

const isImageFile = (file) =>
  file?.type?.startsWith('image/') ||
  /\.(jpe?g|png|gif|webp|heic|heif|bmp|tif{1,2})$/i.test(file?.name || '') ||
  (!file?.type && !isVideoFile(file))

const isCaptureCancelled = (error) => {
  const msg = String(error?.message || error || '').toLowerCase()
  return msg.includes('cancel') || msg.includes('dismiss')
}

async function captureNativePhotoFile() {
  const { Camera, CameraResultType, CameraSource } = await import('@capacitor/camera')
  const photo = await Camera.getPhoto({
    quality: 85,
    resultType: CameraResultType.Uri,
    source: CameraSource.Camera,
    correctOrientation: true,
    saveToGallery: false,
  })
  if (!photo?.webPath) return null
  const res = await fetch(photo.webPath)
  const blob = await res.blob()
  const format = String(photo.format || 'jpeg').toLowerCase().replace('jpg', 'jpeg')
  const ext = format === 'jpeg' ? 'jpg' : format
  return new File([blob], `shipment-photo-${Date.now()}.${ext}`, {
    type: blob.type || `image/${format}`,
  })
}

function MediaThumb({ url, video, onRemove, badge }) {
  return (
    <div className="relative aspect-square rounded-xl overflow-hidden border border-gray-200 bg-gray-50">
      {video ? (
        <video src={url} className="w-full h-full object-cover" muted playsInline />
      ) : (
        <img src={url} alt="" className="w-full h-full object-cover" />
      )}
      <button
        type="button"
        onClick={onRemove}
        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1"
        aria-label="Remove"
      >
        <X className="w-3.5 h-3.5" />
      </button>
      {badge ? (
        <span className="absolute bottom-1 left-1 text-[10px] bg-black/60 text-white px-1.5 py-0.5 rounded">
          {badge}
        </span>
      ) : null}
    </div>
  )
}

/**
 * Multi photo/video picker for booking.
 * files: File[] (new uploads)
 * existing: [{id, url/cdn_url, media_type}] from API
 * removedIds: number[] of existing media to delete on save
 */
const ShipmentMediaUploader = ({
  files = [],
  onFilesChange,
  existing = [],
  removedIds = [],
  onRemovedIdsChange,
  disabled = false,
  compact = false,
  dropzone = false,
}) => {
  const galleryRef = useRef(null)
  const photoRef = useRef(null)
  const videoRef = useRef(null)
  const [showCapture, setShowCapture] = useState(() => {
    if (dropzone) return true
    if (typeof window === 'undefined') return false
    return window.matchMedia(CAPTURE_MQ).matches
  })

  useEffect(() => {
    if (dropzone) {
      setShowCapture(true)
      return undefined
    }
    const mq = window.matchMedia(CAPTURE_MQ)
    const update = () => setShowCapture(mq.matches)
    update()
    mq.addEventListener('change', update)
    return () => mq.removeEventListener('change', update)
  }, [dropzone])

  const visibleExisting = useMemo(
    () => (existing || []).filter((m) => m?.id == null || !removedIds.includes(m.id)),
    [existing, removedIds],
  )

  const photoCount =
    visibleExisting.filter((m) => (m.media_type || 'photo') === 'photo').length +
    files.filter((f) => !isVideoFile(f)).length
  const videoCount =
    visibleExisting.filter((m) => m.media_type === 'video').length +
    files.filter((f) => isVideoFile(f)).length

  const emitError = (msg) => {
    const event = new CustomEvent('shipment-media-error', { detail: msg })
    window.dispatchEvent(event)
    console.warn(msg)
  }

  const addFiles = (picked) => {
    const incoming = Array.from(picked || []).filter(Boolean)
    if (!incoming.length) return

    const next = [...files]
    let photos = photoCount
    let videos = videoCount

    for (const file of incoming) {
      const video = isVideoFile(file)
      if (video) {
        if (file.size > MAX_VIDEO_BYTES) {
          emitError(`Video too large (max 50MB): ${file.name}`)
          continue
        }
        if (videos >= MAX_VIDEOS) {
          emitError(`Maximum ${MAX_VIDEOS} videos allowed`)
          continue
        }
        videos += 1
      } else {
        if (!isImageFile(file)) {
          emitError(`Unsupported file: ${file.name}`)
          continue
        }
        if (file.size > MAX_PHOTO_BYTES) {
          emitError(`Photo too large (max 5MB): ${file.name}`)
          continue
        }
        if (photos >= MAX_PHOTOS) {
          emitError(`Maximum ${MAX_PHOTOS} photos allowed`)
          continue
        }
        photos += 1
      }
      next.push(file)
    }
    onFilesChange?.(next)
  }

  const handlePick = (e) => {
    addFiles(e.target.files)
    e.target.value = ''
  }

  const openGallery = () => {
    if (disabled) return
    galleryRef.current?.click()
  }

  const openPhotoCamera = () => {
    if (disabled) return
    if (isNativeApp()) {
      void (async () => {
        const allowed = await ensureCameraPermission()
        if (!allowed) {
          emitError('Camera permission denied. Enable camera access in app settings.')
          return
        }
        try {
          const file = await captureNativePhotoFile()
          if (file) addFiles([file])
        } catch (error) {
          if (isCaptureCancelled(error)) return
          console.warn('Native photo capture failed, using camera input:', error)
          photoRef.current?.click()
        }
      })()
      return
    }
    photoRef.current?.click()
  }

  const openVideoCamera = () => {
    if (disabled) return
    if (isNativeApp()) {
      void (async () => {
        const allowed = await ensureCameraPermission()
        if (!allowed) {
          emitError('Camera permission denied. Enable camera access in app settings.')
          return
        }
        videoRef.current?.click()
      })()
      return
    }
    videoRef.current?.click()
  }

  const removeNewFile = (index) => {
    onFilesChange?.(files.filter((_, i) => i !== index))
  }

  const removeExisting = (id) => {
    if (id == null) return
    onRemovedIdsChange?.([...removedIds, id])
  }

  const hiddenInputs = (
    <>
      <input
        ref={galleryRef}
        type="file"
        accept={ACCEPT}
        multiple
        className="sr-only"
        disabled={disabled}
        onChange={handlePick}
      />
      <input
        ref={photoRef}
        type="file"
        accept={PHOTO_ACCEPT}
        capture="environment"
        className="sr-only"
        disabled={disabled}
        onChange={handlePick}
      />
      <input
        ref={videoRef}
        type="file"
        accept={VIDEO_ACCEPT}
        capture="environment"
        className="sr-only"
        disabled={disabled}
        onChange={handlePick}
      />
    </>
  )

  const actionBtn = (label, Icon, onClick, extraClass = '') => (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`min-h-[48px] px-2 py-2.5 text-xs sm:text-sm font-semibold rounded-xl border disabled:opacity-50 flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-1.5 ${extraClass}`}
    >
      <Icon className="w-5 h-5 shrink-0" />
      <span>{label}</span>
    </button>
  )

  const captureActions = showCapture ? (
    <div className={`grid grid-cols-3 gap-2 ${dropzone ? 'w-full' : 'w-full sm:w-auto sm:min-w-[280px]'}`}>
      {actionBtn('Photo', CameraIcon, openPhotoCamera, 'border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100')}
      {actionBtn('Video', Video, openVideoCamera, 'border-violet-300 text-violet-700 bg-violet-50 hover:bg-violet-100')}
      {actionBtn('Upload', Upload, openGallery, 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50')}
    </div>
  ) : (
    <button
      type="button"
      disabled={disabled}
      onClick={openGallery}
      className="px-3 py-1.5 text-xs sm:text-sm font-semibold rounded-xl border border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100 disabled:opacity-50"
    >
      Add media
    </button>
  )

  const thumbs = (visibleExisting.length > 0 || files.length > 0) && (
    <div className={`grid gap-2 ${dropzone ? 'grid-cols-3' : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4'}`}>
      {visibleExisting.map((item, idx) => {
        const url = item.cdn_url || item.url
        const video = item.media_type === 'video'
        return (
          <MediaThumb
            key={`ex-${item.id ?? 'legacy'}-${idx}`}
            url={url}
            video={video}
            onRemove={() => removeExisting(item.id)}
            badge={video ? 'Video' : null}
          />
        )
      })}
      {files.map((file, index) => {
        const url = URL.createObjectURL(file)
        const video = isVideoFile(file)
        return (
          <MediaThumb
            key={`new-${index}-${file.name}`}
            url={url}
            video={video}
            onRemove={() => removeNewFile(index)}
            badge={video ? 'New video' : 'New'}
          />
        )
      })}
    </div>
  )

  if (dropzone) {
    return (
      <div className="space-y-2">
        {hiddenInputs}
        {captureActions}
        <p className="text-[10px] sm:text-xs text-gray-500 text-center">
          Take a photo, record a video, or upload from gallery. Up to {MAX_PHOTOS} photos (5MB) and {MAX_VIDEOS} videos (50MB).
        </p>
        {thumbs}
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {hiddenInputs}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className={`${compact ? 'text-xs' : 'text-sm'} font-semibold text-gray-700`}>
            Photos &amp; Videos
          </p>
          <p className="text-[10px] sm:text-xs text-gray-500">
            Up to {MAX_PHOTOS} photos (5MB) and {MAX_VIDEOS} videos (50MB).
          </p>
        </div>
        {showCapture ? null : captureActions}
      </div>
      {showCapture ? captureActions : null}
      {thumbs}
    </div>
  )
}

export default ShipmentMediaUploader
